import { DropDownButton, DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import React, { useEffect, useMemo } from 'react';
import { useDocumentsContext } from '../../../context/documents/DocumentsContext';
import { useSearchContext } from '../../../context/search/SearchContext';
import { GearSearchExportRequest } from '../../../Model/GearSearchExportRequest';
import { useViewsContext } from '../../../context/view/ViewsContext';
import { SvgIcon } from '@progress/kendo-react-common';
import {
  downloadIcon,
  exportIcon,
  lockIcon,
  unlockIcon,
  trashIcon,
  fileAddIcon,
  pencilIcon,
  chevronDownIcon
} from '@progress/kendo-svg-icons';
import { useGlobalizationContext } from '../../../context/globalization/GlobalizationContext';
import {
  CONTENT_TYPE_FIELD,
  DOC_ID_FIELD,
  MAX_ITEMS_LIMIT_EXPORT_CSV,
  TOO_MANY_REQUESTS_EXPORT_CSV
} from '../../../config/consts';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import _ from 'lodash';
import { useLockDocuments } from '../../../context/documents/useLockDocuments';
import { useUnlockDocuments } from '../../../context/documents/useUnlockDocuments';
import { useCurrentUserContext } from '../../../context/currentUser/CurrentUserContext';
import { LockUnlockDocumentsInfoRequest } from '../../../Model/requests/LockUnlockDocumentsInfoRequest';
import { DocumentIdAndCt } from '../../../Model/requests/DocumentIdAndCt';
import { useNotificationContext } from '../../../global/hooks/useNotificationContext';
import { LockUnlockResponse } from '../../../Model/responseDtos/LockUnlockResponse';
import { GEAR_NOTIFICATION_TYPE } from '../../../Model/enums/GearNotificationType';
import { GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT } from '../../../Model/enums/GearUserAllowedActions';
import { useDeleteDocuments } from '../../../context/documents/useDeleteDocuments';
import { DeleteDocumentsRequest } from '../../../Model/requests/DeleteDocumentsRequest';
import { DeleteDocumentsResponse } from '../../../Model/responseDtos/DeleteDocumentsResponse';
import { Label } from '@progress/kendo-react-labels';
import { useGetCtsWithCreatePermission } from '../../../context/contentTypes/useGetCtsWithCreatePermission';
import { useGearDialog } from '../../common/useGearDialog';
import { useDocumentFormContext } from '../../../context/form/DocumentFormContext';
import { useGetView } from '../../../context/view/useGetView';
import { useTranslation } from '../../../context/globalization/useTranslation';
import { useGetUserPermissions } from '../../../context/currentUser/useGetUserPermissions';
import { FilterActionsContextMenu } from './FilterActionsContextMenu';
import {
  ACTION_GROUPS,
  ActionItemId,
  CONFIRM_DIALOG,
  ConfirmDialogType,
  DropDownButtonItem,
  TooManyRequestsTimer
} from './FilterActions.types';

interface FilterActionsProps {
  onMountActions?: (actions: FilterActionsHandle) => void;
}

export interface FilterActionsHandle {
  openContextMenu: (x: number, y: number) => void;
}

export const FilterActions: React.FC<FilterActionsProps> = ({ onMountActions }) => {
  const { data, queryRequest } = useSearchContext();
  const { selectedView } = useViewsContext();
  const { selectedLocale } = useGlobalizationContext();
  const { data: currentView } = useGetView(selectedView, selectedLocale);
  const { selectedDocuments, downloadFiles } = useDocumentsContext();
  const { handleTriggerExportToCsv } = useSearchContext();
  const [dialogVisible, setDialogVisible] = React.useState<ConfirmDialogType | undefined>(undefined);
  const { openNewDocumentForm, openEditDocumentForm } = useDocumentFormContext();
  const [tooManyRequestsTimer, setTooManyRequestsTimer] = React.useState<TooManyRequestsTimer>();
  const { showToast } = useNotificationContext();
  const { showDialog, DialogComponent } = useGearDialog();
  const { selectedIndex } = useCurrentUserContext();
  const { data: ctsWithCreatePermissions } = useGetCtsWithCreatePermission(selectedIndex?.id);
  const { mutate: mutateLockDocuments } = useLockDocuments();
  const { mutate: mutateUnlockDocuments } = useUnlockDocuments();
  const { mutate: mutateDeleteDocuments } = useDeleteDocuments();
  const { data: permissions } = useGetUserPermissions();
  const { translate } = useTranslation(selectedLocale);
  const [contextMenuPosition, setContextMenuPosition] = React.useState<{ x: number; y: number } | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tooManyRequestsTimer || !tooManyRequestsTimer?.timeLeft) {
      if (tooManyRequestsTimer && tooManyRequestsTimer.timeLeft === 0) {
        setTooManyRequestsTimer(undefined);
        if (dialogVisible === CONFIRM_DIALOG.TOO_MANY_REQUESTS) {
          setDialogVisible(undefined);
        }
      }
      return () => {};
    }

    const intervalId = setInterval(() => {
      setTooManyRequestsTimer({
        ...tooManyRequestsTimer,
        timeLeft: tooManyRequestsTimer.timeLeft - 1
      });
    }, 1000);

    // clear interval on re-render to avoid memory leaks
    return () => clearInterval(intervalId);
  }, [dialogVisible, tooManyRequestsTimer, tooManyRequestsTimer?.timeLeft]);

  const actionsRef = React.useRef({
    openContextMenu: (x: number, y: number) => {
      setContextMenuPosition({ x, y });
    }
  });

  const handleContainerRef = (node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (node && onMountActions) {
      onMountActions(actionsRef.current);
    }
  };

  const isAddHidden = ctsWithCreatePermissions === undefined || ctsWithCreatePermissions.length === 0;

  const selectedDocArray = React.useMemo(() => selectedDocuments || [], [selectedDocuments]);

  const items: DropDownButtonItem[] = useMemo(() => {
    const result = [
      {
        id: ActionItemId.GROUP,
        text: translate('custom.toolbar.actions.documentGroup'),
        className: 'filterActionButtonGroupOption',
        groupId: ACTION_GROUPS.DOCUMENT_GROUP
      },
      {
        id: ActionItemId.ADD_NEW,
        text: translate('custom.toolbar.actions.form.addNewTitle'),
        svgIcon: fileAddIcon,
        groupId: ACTION_GROUPS.DOCUMENT_GROUP,
        isHidden: isAddHidden
      },
      {
        id: ActionItemId.GROUP,
        text: translate('custom.toolbar.actions.gridGroup'),
        className: 'filterActionButtonGroupOption',
        groupId: ACTION_GROUPS.GRID_GROUP
      },
      {
        id: ActionItemId.EXPORT,
        text: translate('custom.toolbar.actions.export.action'),
        svgIcon: exportIcon,
        groupId: ACTION_GROUPS.GRID_GROUP,
        isHidden: !permissions?.csvExport
      }
    ] as DropDownButtonItem[];
    if (selectedDocArray.length > 0) {
      result.splice(2, 0, {
        id: ActionItemId.DOWNLOAD,
        text: translate(
          selectedDocArray.length === 1
            ? 'custom.toolbar.actions.download.downloadFile'
            : 'custom.toolbar.actions.download.downloadZip'
        ),
        svgIcon: downloadIcon,
        groupId: ACTION_GROUPS.DOCUMENT_GROUP,
        isHidden: !selectedDocArray.find(selectedDoc =>
          selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD)
        )
      });
      result.splice(2, 0, {
        id: ActionItemId.LOCK,
        text: translate('custom.toolbar.actions.lock.lockTitle'),
        svgIcon: lockIcon,
        groupId: ACTION_GROUPS.DOCUMENT_GROUP,
        isHidden: !selectedDocArray.some(selectedDoc =>
          selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.LOCK)
        )
      });
      result.splice(2, 0, {
        id: ActionItemId.UNLOCK,
        text: translate('custom.toolbar.actions.lock.unlockTitle'),
        svgIcon: unlockIcon,
        groupId: ACTION_GROUPS.DOCUMENT_GROUP,
        isHidden: !selectedDocArray.some(selectedDoc =>
          selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.LOCK)
        )
      });
      result.splice(2, 0, {
        id: ActionItemId.DELETE,
        text: translate('custom.toolbar.actions.delete.title'),
        svgIcon: trashIcon,
        groupId: ACTION_GROUPS.DOCUMENT_GROUP,
        isHidden: !selectedDocArray.some(selectedDoc =>
          selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.DELETE)
        )
      });
      if (selectedDocArray.length === 1) {
        result.splice(2, 0, {
          id: ActionItemId.EDIT,
          text: translate('custom.toolbar.actions.form.editTitle'),
          svgIcon: pencilIcon,
          groupId: ACTION_GROUPS.DOCUMENT_GROUP,
          isHidden: !selectedDocArray.find(selectedDoc =>
            selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.UPDATE)
          )
        });
      }
    }

    const groupsWithVisibleItems = new Set<string>();
    result.forEach(item => {
      if (item.id !== ActionItemId.GROUP && !item.isHidden && item.groupId) {
        groupsWithVisibleItems.add(item.groupId);
      }
    });

    const filteredResult = result.filter(item => {
      if (item.id === ActionItemId.GROUP) {
        return item.groupId && groupsWithVisibleItems.has(item.groupId);
      }
      return !item.isHidden;
    });

    if (filteredResult.length === 0) {
      const noActionsResult: DropDownButtonItem[] = [
        {
          id: ActionItemId.NO_ACTIONS,
          text: translate('custom.toolbar.actions.noActions') || 'No actions available'
        }
      ] as DropDownButtonItem[];

      return noActionsResult;
    }
    return filteredResult;
  }, [translate, isAddHidden, permissions?.csvExport, selectedDocArray]);

  const itemClicked = async (event: DropDownButtonItemClickEvent) => {
    if (event.item.isDisabled || event.item.id === ActionItemId.GROUP) {
      return undefined;
    }
    switch (event.item.id) {
      case ActionItemId.DOWNLOAD:
        if (downloadFiles && selectedDocuments && selectedIndex) {
          const changed = selectedDocuments.filter(selectedDoc =>
            selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD)
          ).length;

          const skipped = selectedDocuments.filter(
            selectedDoc => !selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD)
          ).length;

          const message =
            translate('custom.toolbar.actions.download.downloadDialogMessage', {
              changed
            }) +
            (skipped !== 0
              ? translate('custom.toolbar.actions.download.skippedDownloadDialogMessage', {
                  skipped
                })
              : '') +
            translate('custom.toolbar.actions.download.downloadProceed');
          showDialog({
            title: translate('custom.toolbar.actions.download.downloadDialogTitle'),
            message,
            confirmButtonLabel: translate('custom.common.confirm'),
            discardButtonLabel: translate('custom.common.discard'),
            onConfirm: confirmed => {
              if (confirmed) {
                return downloadFiles();
              }
              return undefined;
            }
          });
        }
        return undefined;
      case ActionItemId.LOCK:
        if (selectedDocuments && selectedIndex) {
          const changed = selectedDocuments.filter(selectedDoc =>
            selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.LOCK)
          ).length;

          const skipped = selectedDocuments.filter(
            selectedDoc => !selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.LOCK)
          ).length;

          const documents: DocumentIdAndCt[] = selectedDocuments.map(i => ({
            contentTypeName: i[CONTENT_TYPE_FIELD],
            documentId: i[DOC_ID_FIELD]
          }));

          const message =
            translate('custom.toolbar.actions.lock.lockDialogMessage', {
              changed
            }) +
            (skipped !== 0
              ? translate('custom.toolbar.actions.lock.skippedLockDialogMessage', {
                  skipped
                })
              : '') +
            translate('custom.toolbar.actions.lock.lockProceed');

          const dto: LockUnlockDocumentsInfoRequest = { documents, indexId: selectedIndex.id };
          showDialog({
            title: translate('custom.toolbar.actions.lock.lockDialogTitle'),
            message,
            confirmButtonLabel: translate('custom.common.confirm'),
            discardButtonLabel: translate('custom.common.discard'),
            onConfirm: confirmed => {
              if (confirmed) {
                return mutateLockDocuments(
                  { dto },
                  {
                    onSuccess: (response: LockUnlockResponse) => {
                      const changedCount = response.changed.length;
                      const skippedCount = response.skipped.length;
                      let messageTemp = '';
                      let type = undefined;
                      if (skippedCount > 0) {
                        type = GEAR_NOTIFICATION_TYPE.INFO;
                        messageTemp = translate('custom.toolbar.actions.lock.partialLock', {
                          changedCount,
                          skippedCount
                        });
                      } else {
                        type = GEAR_NOTIFICATION_TYPE.SUCCESS;
                        messageTemp = translate('custom.toolbar.actions.lock.successfullLock');
                      }
                      showToast({
                        type,
                        message: messageTemp
                      });
                    }
                  }
                );
              }
              return undefined;
            }
          });
        }
        return undefined;
      case ActionItemId.UNLOCK:
        if (selectedDocuments && selectedIndex) {
          const changed = selectedDocuments.filter(selectedDoc =>
            selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.LOCK)
          ).length;

          const skipped = selectedDocuments.filter(
            selectedDoc => !selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.LOCK)
          ).length;

          const documents: DocumentIdAndCt[] = selectedDocuments.map(i => ({
            contentTypeName: i[CONTENT_TYPE_FIELD],
            documentId: i[DOC_ID_FIELD]
          }));

          const message =
            translate('custom.toolbar.actions.lock.unlockDialogMessage', {
              changed
            }) +
            (skipped !== 0
              ? translate('custom.toolbar.actions.lock.skippedUnlockDialogMessage', {
                  skipped
                })
              : '') +
            translate('custom.toolbar.actions.lock.unlockProceed');

          const dto: LockUnlockDocumentsInfoRequest = { documents, indexId: selectedIndex.id };
          showDialog({
            title: translate('custom.toolbar.actions.lock.unlockDialogTitle'),
            message,
            confirmButtonLabel: translate('custom.common.confirm'),
            discardButtonLabel: translate('custom.common.discard'),
            onConfirm: confirmed => {
              if (confirmed) {
                return mutateUnlockDocuments(
                  { dto },
                  {
                    onSuccess: (response: LockUnlockResponse) => {
                      const changedCount = response.changed.length;
                      const skippedCount = response.skipped.length;
                      let messageTemp = '';
                      let type = undefined;
                      if (skippedCount > 0) {
                        type = GEAR_NOTIFICATION_TYPE.INFO;
                        messageTemp = translate('custom.toolbar.actions.lock.partialUnlock', {
                          changedCount,
                          skippedCount
                        });
                      } else {
                        type = GEAR_NOTIFICATION_TYPE.SUCCESS;
                        messageTemp = translate('custom.toolbar.actions.lock.successfullUnlock');
                      }
                      showToast({
                        type,
                        message: messageTemp
                      });
                    }
                  }
                );
              }
              return undefined;
            }
          });
        }
        return undefined;
      case ActionItemId.EXPORT:
        if (handleTriggerExportToCsv) {
          if (data && data.totalCount > MAX_ITEMS_LIMIT_EXPORT_CSV) {
            setDialogVisible(CONFIRM_DIALOG.MAX_EXPORT_ITEMS);
            return undefined;
          }

          const querySearchExport: GearSearchExportRequest = {
            filters: queryRequest?.filters,
            actionsFilter: queryRequest?.actionsFilter,
            searchText: queryRequest?.searchText,
            searchFields: queryRequest?.searchFields,
            orderBy: queryRequest?.orderBy,
            orderByIsDescending: queryRequest?.orderByIsDescending,
            select: currentView?.fields.map(column => column.name),
            locale: selectedLocale,
            viewId: selectedView
          };

          if (tooManyRequestsTimer && _.isEqual(querySearchExport, tooManyRequestsTimer.querySearchExport)) {
            setDialogVisible(CONFIRM_DIALOG.TOO_MANY_REQUESTS);
            return undefined;
          }

          setTooManyRequestsTimer({ querySearchExport, timeLeft: TOO_MANY_REQUESTS_EXPORT_CSV });

          return handleTriggerExportToCsv(querySearchExport);
        }
        return undefined;
      case ActionItemId.DELETE:
        if (selectedDocuments && selectedIndex) {
          const documents: DocumentIdAndCt[] = selectedDocuments.map(i => ({
            contentTypeName: i[CONTENT_TYPE_FIELD],
            documentId: i[DOC_ID_FIELD]
          }));
          const dto: DeleteDocumentsRequest = { documents, indexId: selectedIndex.id };
          const changed = selectedDocuments.filter(selectedDoc =>
            selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.DELETE)
          ).length;

          const skipped = selectedDocuments.filter(
            selectedDoc => !selectedDoc.Cps_Permissions.includes(GEAR_ACTIONS_ALLOWED_FOR_DOCUMENT.DELETE)
          ).length;

          const messageText =
            translate('custom.toolbar.actions.delete.deleteDialogMessage', {
              changed
            }) +
            (skipped !== 0
              ? translate('custom.toolbar.actions.delete.skippedDeleteDialogMessage', {
                  skipped
                })
              : '') +
            translate('custom.toolbar.actions.delete.deleteProceed');

          showDialog({
            title: translate('custom.toolbar.actions.delete.deleteDialogTitle'),
            message: messageText,
            confirmButtonLabel: translate('custom.common.confirm'),
            discardButtonLabel: translate('custom.common.discard'),
            onConfirm: confirmed => {
              if (confirmed) {
                return mutateDeleteDocuments(
                  { dto },
                  {
                    onSuccess: (response: DeleteDocumentsResponse) => {
                      const deletedCount = response.deleted.length;
                      const skippedCount = response.skipped.length;
                      let message = '';
                      let type = undefined;
                      if (skippedCount > 0) {
                        type = GEAR_NOTIFICATION_TYPE.INFO;
                        message = translate('custom.toolbar.actions.delete.partialDelete', {
                          deletedCount,
                          skippedCount
                        });
                      } else {
                        type = GEAR_NOTIFICATION_TYPE.SUCCESS;
                        message = translate('custom.toolbar.actions.delete.successfullDelete');
                      }
                      showToast({
                        type,
                        message
                      });
                    }
                  }
                );
              }
              return undefined;
            }
          });
        }
        return undefined;
      case ActionItemId.ADD_NEW:
        openNewDocumentForm();
        return undefined;
      case ActionItemId.EDIT: {
        if (selectedDocArray.length !== 1) {
          return undefined;
        }
        const selectedDocument = selectedDocArray[0];
        openEditDocumentForm({
          [CONTENT_TYPE_FIELD]: selectedDocument[CONTENT_TYPE_FIELD],
          [DOC_ID_FIELD]: selectedDocument[DOC_ID_FIELD]
        });
        return undefined;
      }
      default:
        return undefined;
    }
  };

  const handleItemRender = (li: { item: DropDownButtonItem; itemIndex: number }) => {
    return (
      <div className={li.item.className || `filterActionButtonOption`}>
        {li.item.svgIcon && <SvgIcon icon={li.item.svgIcon} />}
        <Label key={li.item.id}>{li.item.text}</Label>
      </div>
    );
  };

  const handleContextMenuItemClick = (item: DropDownButtonItem) => {
    setContextMenuPosition(null);
    itemClicked({
      item,
      itemIndex: 0,
      target: null as any,
      syntheticEvent: null as any,
      nativeEvent: null as any
    });
  };

  const timerSuffix = tooManyRequestsTimer ? ` (${tooManyRequestsTimer.timeLeft}s)` : '';

  return (
    <div>
      <div ref={handleContainerRef}>
        <DropDownButton
          endIcon={<SvgIcon icon={chevronDownIcon} />}
          text={translate('custom.toolbar.actions.title')}
          items={items}
          itemRender={handleItemRender}
          size={'medium'}
          themeColor={'base'}
          fillMode="solid"
          onItemClick={itemClicked}
          popupSettings={{
            popupClass: 'filterActionButtonPopup'
          }}
        />
      </div>

      <FilterActionsContextMenu
        position={contextMenuPosition}
        items={items.filter(x => x.groupId !== ACTION_GROUPS.GRID_GROUP)}
        onItemClick={handleContextMenuItemClick}
        onClose={() => setContextMenuPosition(null)}
      />

      {dialogVisible && (
        <ConfirmDialog
          className="filterActionsDialog"
          title={translate(
            dialogVisible === CONFIRM_DIALOG.MAX_EXPORT_ITEMS
              ? 'custom.toolbar.actions.export.maxExportItems.dialogHeader'
              : 'custom.toolbar.actions.export.tooManyRequests.dialogHeader'
          )}
          message={`${translate(
            dialogVisible === CONFIRM_DIALOG.MAX_EXPORT_ITEMS
              ? 'custom.toolbar.actions.export.maxExportItems.dialogBody'
              : 'custom.toolbar.actions.export.tooManyRequests.dialogBody'
          )}${timerSuffix}`}
          confirmButtonOnly={true}
          onConfirm={() => setDialogVisible(undefined)}
        />
      )}
      {DialogComponent}
    </div>
  );
};
